import React, { useState, useEffect, useRef } from 'react';
import { Facebook as LucideFacebook, Instagram as LucideInstagram, Menu as LucideMenu, X as LucideX, Trash2 as LucideTrash2, ChevronDown as LucideChevronDown, ChevronUp as LucideChevronUp } from 'lucide-react';

// List of common country codes and dial codes with added phone_length for validation
// Note: Phone number lengths can vary greatly by country and region.
// For a production application, a more comprehensive and regularly updated
// database of phone number formats would be recommended.
const countryCodes = [
    { code: 'US', dial_code: '+1', name: 'United States', phone_length: 10 },
    { code: 'CA', dial_code: '+1', name: 'Canada', phone_length: 10 },
    { code: 'GB', dial_code: '+44', name: 'United Kingdom', phone_length: [7,10] }, // UK mobile numbers are typically 10 digits after 07, landlines vary. Using 10 as a common length.
    { code: 'AU', dial_code: '+61', name: 'Australia', phone_length: [5,15] }, // Typically 9 digits after 0
    { code: 'BW', dial_code: '+267', name: 'Botswana', phone_length: 8 }, // Updated to 8 digits for Botswana (single value, not array)
    { code: 'ZA', dial_code: '+27', name: 'South Africa', phone_length: 9 },
    { code: 'ZM', dial_code: '+260', name: 'Zambia', phone_length: 9 },
    { code: 'ZW', dial_code: '+263', name: 'Zimbabwe', phone_length: [5,10] },
    { code: 'NA', dial_code: '+264', name: 'Namibia', phone_length: [6,10]},
    { code: 'DE', dial_code: '+49', name: 'Germany', phone_length: [6,13] }, // Updated to max 11 digits
    { code: 'FR', dial_code: '+33', name: 'France', phone_length: 9 },
    { code: 'ES', dial_code: '+34', name: 'Spain', phone_length: 9 },
    { code: 'IT', dial_code: '+39', name: 'Italy', phone_length: 11 }, // Updated to max 10 digits
    { code: 'NL', dial_code: '+31', name: 'Netherlands', phone_length: 9 },
    { code: 'CH', dial_code: '+41', name: 'Switzerland', phone_length: [4,12] },
    { code: 'SE', dial_code: '+46', name: 'Sweden', phone_length: [7,13] }, // Updated to max 9 digits
    { code: 'NO', dial_code: '+47', name: 'Norway', phone_length: [5,8] },
    { code: 'DK', dial_code: '+45', name: 'Denmark', phone_length: 8 },
    { code: 'IE', dial_code: '+353', name: 'Ireland', phone_length: [7,11] }, // Updated to max 9 digits
    { code: 'NZ', dial_code: '+64', name: 'New Zealand', phone_length: [3,10] },
    { code: 'KE', dial_code: '+254', name: 'Kenya', phone_length: [6,10] },
    { code: 'TZ', dial_code: '+255', name: 'Tanzania', phone_length: 9 },
    { code: 'UG', dial_code: '+256', name: 'Uganda', phone_length: 9 },
    { code: 'RW', dial_code: '+250', name: 'Rwanda', phone_length: 9 },
    { code: 'MW', dial_code: '+265', name: 'Malawi', phone_length: [7,8] },
    { code: 'MZ', dial_code: '+258', name: 'Mozambique', phone_length: [8,9] },
    { code: 'TH', dial_code: '+66', name: 'Thailand', phone_length: [8,9] },
    { code: 'RU', dial_code: '+7', name: 'Russia', phone_length: 10 },
    { code: 'CN', dial_code: '+86', name: 'China', phone_length: [5,12] },
    { code: 'JP', dial_code: '+81', name: 'Japan', phone_length: [5,13] }, // Updated to max 10 digits
    { code: 'VN', dial_code: '+84', name: 'Vietnam', phone_length: [7,10] }, // Updated to max 9 digits
    { code: 'AT', dial_code: '+43', name: 'Austria', phone_length: [4,13] }, // Updated to max 11 digits
    { code: 'AE', dial_code: '+971', name: 'United Arab Emirates', phone_length: [8,9] },
    { code: 'OTHER', dial_code: 'Other', name: 'Other (Type Manually)', phone_length: [7, 15] }, // Reverted to a more reasonable range
];

// List of available activities
const availableActivities = [
    'Accommodation Booking',
    'Airport Transfer',
    'Recreational Fishing',
    'Riverboat Cruise',
    'Birdwatching',
    'Game drive',
    'Mobile Safari',
    'Boat Transfer',
    'Victoria Falls Day Trip',
    'Cultural Village Tour'
];

// Activities that require a time slot
const activitiesRequiringTimeSlot = [
    'Recreational Fishing',
    'Riverboat Cruise',
    'Birdwatching',
    'Game drive'
];

// Locations for Airport Transfers (From Options)
const fromTransferLocations = [
    'Kasane Airport (BBK)',
    'Victoria Falls Airport (VFA)',
    'Specify Hotel/Lodge in Kasane/Kazungula'
];

// Locations for Airport Transfers (To Options)
const toTransferLocations = [
    'Kasane Airport (BBK)',
    'Victoria Falls Airport (VFA)',
    'Specify Hotel/Lodge in Kasane/Kazungula'
];

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
};

// Modal Component for custom alerts
const Modal = ({ message, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-inter">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-sm w-full text-center transform transition-all duration-300 scale-100 opacity-100">
                <p className="text-xl font-semibold text-stone-800 mb-6">{message}</p>
                <button
                    onClick={onClose}
                    className="bg-emerald-800 text-white px-6 py-2 rounded-full text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md"
                >
                    Close
                </button>
            </div>
        </div>
    );
};

// Custom PhoneCodeSelect Component
const PhoneCodeSelect = ({ name, value, onChange, countryCodes, customPhoneCode, onCustomPhoneCodeChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Sort country codes alphabetically by name, keeping 'Other' at the end
    const sortedCountryCodes = [...countryCodes].sort((a, b) => {
        if (a.code === 'OTHER') return 1;
        if (b.code === 'OTHER') return -1;
        return a.name.localeCompare(b.name);
    });

    // Find the currently selected country name for display
    const selectedCountry = sortedCountryCodes.find(country => country.dial_code === value);
    const displayValue = value === 'Other' ? 'Other (Type Manually)' : (selectedCountry ? selectedCountry.dial_code : '+267');

    // Close dropdown if clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (dialCode) => {
        onChange({ target: { name, value: dialCode } });
        if (dialCode !== 'Other') {
            onCustomPhoneCodeChange({ target: { name: 'customPhoneCode', value: '' } }); // Clear custom if not 'Other'
        }
        setIsOpen(false);
    };

    return (
        <div className="relative w-24" ref={dropdownRef}>
            <div
                className="flex items-center justify-between p-2 md:p-3 border border-stone-300 rounded-lg bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{displayValue}</span>
                {isOpen ? <LucideChevronUp className="w-4 h-4 text-stone-600" /> : <LucideChevronDown className="w-4 h-4 text-stone-600" />}
            </div>
            {isOpen && (
                <ul className="absolute z-10 w-48 bg-white border border-stone-300 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                    {sortedCountryCodes.map((country) => (
                        <li
                            key={country.code}
                            className={`p-2 md:p-3 cursor-pointer hover:bg-stone-100 ${value === country.dial_code ? 'bg-stone-100 font-semibold' : ''}`}
                            onClick={() => handleSelect(country.dial_code)}
                        >
                            {country.dial_code === 'Other' ? country.name : `${country.dial_code} ${country.name}`}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};


// Main App Component
const App = () => {
    // State to manage the active section for navigation
    const [activeSection, setActiveSection] = useState('home');
    // State to manage mobile menu visibility
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    // State to manage contact form data
    const [contactFormData, setContactFormData] = useState({
        name: '',
        phoneCode: '+267',
        customPhoneCode: '',
        phoneNumber: '',
        email: '',
        message: ''
    });
    // State to manage overall booking form data (including added activities)
    const [bookingFormData, setBookingFormData] = useState({
        fullName: '',
        email: '',
        phoneCode: '+267',
        customPhoneCode: '',
        phoneNumber: '',
        bookedActivities: [], // Array to store configured activities
        guests: 1
    });
    // State for the activity currently being configured
    const [currentActivitySelection, setCurrentActivitySelection] = useState({
        activityName: '',
        startDate: '',
        endDate: '',
        timeSlot: '',
        transferDate: '',
        transferTime: '',
        fromLocation: '',
        customFromLocation: '',
        toLocation: '',
        customToLocation: ''
    });
    // State to manage modal visibility and message
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');

    // Constant for the "Specify Hotel/Lodge" option text
    const SPECIFY_HOTEL_LODGE_OPTION = 'Specify Hotel/Lodge in Kasane/Kazungula';

    // Determine if the current activity selection requires a time slot
    const currentActivityRequiresTimeSlot = activitiesRequiringTimeSlot.includes(currentActivitySelection.activityName);

    // Function to check if the current activity's time details are filled
    const isCurrentActivityTimeDetailsFilled = () => {
        if (currentActivitySelection.activityName === 'Airport Transfer') {
            return (
                currentActivitySelection.transferDate &&
                currentActivitySelection.transferTime &&
                currentActivitySelection.fromLocation &&
                (currentActivitySelection.fromLocation !== SPECIFY_HOTEL_LODGE_OPTION || currentActivitySelection.customFromLocation) &&
                currentActivitySelection.toLocation &&
                (currentActivitySelection.toLocation !== SPECIFY_HOTEL_LODGE_OPTION || currentActivitySelection.customToLocation)
            );
        } else {
            return (
                currentActivitySelection.activityName &&
                currentActivitySelection.startDate &&
                currentActivitySelection.endDate &&
                (!currentActivityRequiresTimeSlot || currentActivitySelection.timeSlot)
            );
        }
    };

    // Function to handle navigation clicks
    const navigateTo = (sectionId) => {
        setActiveSection(sectionId);
        setIsMobileMenuOpen(false); // Close mobile menu when navigating
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    };

    // Handle contact form input changes
    const handleContactInputChange = (e) => {
        const { name, value } = e.target;
        setContactFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle input changes for the current activity being configured
    const handleCurrentActivityInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentActivitySelection(prevData => {
            let newData = { ...prevData, [name]: value };

            // Reset specific fields if activity changes
            if (name === 'activityName') {
                newData = {
                    ...newData,
                    startDate: '',
                    endDate: '',
                    timeSlot: '',
                    transferDate: '',
                    transferTime: '',
                    fromLocation: '',
                    customFromLocation: '',
                    toLocation: '',
                    customToLocation: ''
                };
            }

            // If activityName changes, and the new activity doesn't require a time slot, clear the timeSlot
            if (name === 'activityName' && !activitiesRequiringTimeSlot.includes(value)) {
                newData.timeSlot = '';
            }

            // Clear custom location fields if the 'Specify' option is deselected
            if (name === 'fromLocation' && value !== SPECIFY_HOTEL_LODGE_OPTION) {
                newData.customFromLocation = '';
            }
            if (name === 'toLocation' && value !== SPECIFY_HOTEL_LODGE_OPTION) {
                newData.customToLocation = '';
            }

            return newData;
        });
    };

    // Handle adding a configured activity to the bookedActivities list
    const handleAddActivity = () => {
        if (isCurrentActivityTimeDetailsFilled()) {
            setBookingFormData(prevData => {
                const updatedActivities = [...prevData.bookedActivities, currentActivitySelection];
                console.log('Activities after adding:', updatedActivities); // Debugging log
                return {
                    ...prevData,
                    bookedActivities: updatedActivities
                };
            });
            // Reset current activity selection for the next entry
            setCurrentActivitySelection({
                activityName: '',
                startDate: '',
                endDate: '',
                timeSlot: '',
                transferDate: '',
                transferTime: '',
                fromLocation: '',
                customFromLocation: '',
                toLocation: '',
                customToLocation: ''
            });
            setModalMessage('Activity added to your booking!');
            setShowModal(true);
        } else {
            setModalMessage('Please fill in all required details before adding the activity.');
            setShowModal(true);
        }
    };

    // Handle removing an activity from the bookedActivities list
    const handleRemoveActivity = (indexToRemove) => {
        setBookingFormData(prevData => ({
            ...prevData,
            bookedActivities: prevData.bookedActivities.filter((_, index) => index !== indexToRemove)
        }));
        setModalMessage('Activity removed from booking.');
        setShowModal(true);
    };

    // Helper to get the full phone code (including custom if 'Other' is selected)
    const getFullPhoneCode = (formData) => {
        return formData.phoneCode === 'Other' ? formData.customPhoneCode : formData.phoneCode;
    };

    // Function to validate phone number length based on selected country code
    const validatePhoneNumberLength = (phoneNumber, phoneCode, customPhoneCode) => {
        const selectedDialCode = phoneCode === 'Other' ? customPhoneCode : phoneCode;
        const country = countryCodes.find(c => c.dial_code === selectedDialCode);
        const phoneLen = phoneNumber.length;

        if (!country || !country.phone_length) {
            // Default general range if no specific length is defined
            return phoneLen >= 7 && phoneLen <= 15;
        }

        if (Array.isArray(country.phone_length)) {
            // If phone_length is a range [min, max]
            const [minLength, maxLength] = country.phone_length;
            return phoneLen >= minLength && phoneLen <= maxLength;
        } else {
            // If phone_length is a single number
            return phoneLen === country.phone_length;
        }
    };

    // Function to check if the main booking form is valid
    const isBookingFormFullyValid = () => {
        const { fullName, email, phoneNumber, bookedActivities, guests, phoneCode, customPhoneCode } = bookingFormData;

        const isFullNameValid = fullName && fullName.match(/^[A-Za-z\s]+$/);
        const isEmailValid = email && email.match(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/);
        const isPhoneCodeValid = phoneCode !== 'Other' || (customPhoneCode && customPhoneCode.match(/^\+\d{1,4}$/));
        const isPhoneNumberFormatValid = phoneNumber && phoneNumber.match(/^[0-9]+$/); // Only digits
        const isPhoneNumberLengthValid = isPhoneNumberFormatValid && validatePhoneNumberLength(phoneNumber, phoneCode, customPhoneCode);
        const isGuestsValid = guests >= 1;
        const areActivitiesBooked = bookedActivities.length > 0;

        return isFullNameValid && isEmailValid && isPhoneCodeValid && isPhoneNumberFormatValid && isPhoneNumberLengthValid && isGuestsValid && areActivitiesBooked;
    };


    // Handle contact form submission
    const handleContactSubmit = (e) => {
        e.preventDefault();

        const { name, phoneNumber, email, message, phoneCode, customPhoneCode } = contactFormData;

        // Validation for contact form
        if (!name || !name.match(/^[A-Za-z\s]+$/)) {
            setModalMessage('Please enter a valid name (only alphabetic characters and spaces).');
            setShowModal(true);
            return;
        }
        if (!email || !email.match(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)) {
            setModalMessage('Please enter a valid email address (e.g., example@domain.com).');
            setShowModal(true);
            return;
        }
        if (phoneCode === 'Other' && (!customPhoneCode || !customPhoneCode.match(/^\+\d{1,4}$/))) {
            setModalMessage('Please enter a valid custom dial code starting with "+" (e.g., +1, +44).');
            setShowModal(true);
            return;
        }
        if (!phoneNumber || !phoneNumber.match(/^[0-9]+$/)) {
            setModalMessage('Please enter a valid phone number (digits only).');
            setShowModal(true);
            return;
        }
        if (!validatePhoneNumberLength(phoneNumber, phoneCode, customPhoneCode)) {
            const selectedCountry = countryCodes.find(c => c.dial_code === (phoneCode === 'Other' ? customPhoneCode : phoneCode));
            let expectedLengthMsg = 'between 7 and 15 digits.';
            if (selectedCountry && selectedCountry.phone_length) {
                if (Array.isArray(selectedCountry.phone_length)) {
                    expectedLengthMsg = `between ${selectedCountry.phone_length[0]} and ${selectedCountry.phone_length[1]} digits.`;
                } else {
                    expectedLengthMsg = `${selectedCountry.phone_length} digits.`;
                }
            }
            setModalMessage(`Phone number length for ${selectedCountry ? selectedCountry.name : 'your selected country'} must be ${expectedLengthMsg}`);
            setShowModal(true);
            return;
        }
        if (!message) {
            setModalMessage('Please enter your message.');
            setShowModal(true);
            return;
        }

        const phoneCodeToUse = getFullPhoneCode(contactFormData);
        const fullPhoneNumber = phoneNumber ? `${phoneCodeToUse}${phoneNumber}` : 'N/A';

        const contactSubject = encodeURIComponent(`Contact Message from ${name}`);
        const contactBody = encodeURIComponent(
            `Name: ${name}\n` +
            `Phone: ${fullPhoneNumber}\n` +
            `Email: ${email}\n` +
            `Message: ${message}`
        );
        // Only send to email, WhatsApp functionality removed as per request
        window.open(`mailto:Selerioturner@gmail.com?subject=${contactSubject}&body=${contactBody}`, '_blank');

        setModalMessage('Contact message initiated! Please check your email client to send.');
        setShowModal(true);
        setContactFormData({ name: '', phoneCode: '+267', customPhoneCode: '', phoneNumber: '', email: '', message: '' });
    };

    // Handle main booking form submission
    const handleBookingSubmit = (e) => {
        e.preventDefault();

        const { fullName, email, phoneNumber, bookedActivities, guests, phoneCode, customPhoneCode } = bookingFormData;

        // Validation for booking form
        if (!fullName || !fullName.match(/^[A-Za-z\s]+$/)) {
            setModalMessage('Please enter a valid full name (only alphabetic characters and spaces).');
            setShowModal(true);
            return;
        }
        if (!email || !email.match(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)) {
            setModalMessage('Please enter a valid email address (e.g., example@domain.com).');
            setShowModal(true);
            return;
        }
        if (phoneCode === 'Other' && (!customPhoneCode || !customPhoneCode.match(/^\+\d{1,4}$/))) {
            setModalMessage('Please enter a valid custom dial code starting with "+" (e.g., +1, +44).');
            setShowModal(true);
            return;
        }
        if (!phoneNumber || !phoneNumber.match(/^[0-9]+$/)) {
            setModalMessage('Please enter a valid phone number (digits only).');
            setShowModal(true);
            return;
        }
        if (!validatePhoneNumberLength(phoneNumber, phoneCode, customPhoneCode)) {
            const selectedCountry = countryCodes.find(c => c.dial_code === (phoneCode === 'Other' ? customPhoneCode : phoneCode));
            let expectedLengthMsg = 'between 7 and 15 digits.';
            if (selectedCountry && selectedCountry.phone_length) {
                if (Array.isArray(selectedCountry.phone_length)) {
                    expectedLengthMsg = `between ${selectedCountry.phone_length[0]} and ${selectedCountry.phone_length[1]} digits.`;
                } else {
                    expectedLengthMsg = `${selectedCountry.phone_length} digits.`;
                }
            }
            setModalMessage(`Phone number length for ${selectedCountry ? selectedCountry.name : 'your selected country'} must be ${expectedLengthMsg}`);
            setShowModal(true);
            return;
        }
        if (guests < 1) {
            setModalMessage('Number of guests must be at least 1.');
            setShowModal(true);
            return;
        }
        if (bookedActivities.length === 0) {
            setModalMessage('Please add at least one activity to your booking.');
            setShowModal(true);
            return;
        }

        const phoneCodeToUse = getFullPhoneCode(bookingFormData);
        const fullPhoneNumber = phoneNumber ? `${phoneCodeToUse}${phoneNumber}` : 'N/A';

        let activitiesSummary = bookedActivities.map((activity, index) => {
            if (activity.activityName === 'Airport Transfer') {
                const formattedDate = formatDate(activity.transferDate);
                const from = activity.fromLocation === SPECIFY_HOTEL_LODGE_OPTION ? activity.customFromLocation : activity.fromLocation;
                const to = activity.toLocation === SPECIFY_HOTEL_LODGE_OPTION ? activity.customToLocation : activity.toLocation;
                return `${index + 1}. ${activity.activityName} on ${formattedDate} at ${activity.transferTime} from ${from} to ${to}`;
            } else {
                const formattedStartDate = formatDate(activity.startDate);
                const formattedEndDate = formatDate(activity.endDate);
                const timeDetail = activity.timeSlot ? ` (${activity.timeSlot})` : '';
                return `${index + 1}. ${activity.activityName} from ${formattedStartDate} to ${formattedEndDate}${timeDetail}`;
            }
        }).join('\n');

        const bookingSubject = encodeURIComponent(`Safari Booking Request from ${fullName}`);
        const bookingBody = encodeURIComponent(
            `Full Name: ${fullName}\n` +
            `Email: ${email}\n` +
            `Phone: ${fullPhoneNumber}\n` +
            `Number of Guests: ${guests}\n\n` +
            `Booked Activities:\n${activitiesSummary}\n\n` +
            `Please confirm this booking.`
        );
        // Only send to email, WhatsApp functionality removed as per request
        window.open(`mailto:Selerioturner@gmail.com?subject=${bookingSubject}&body=${bookingBody}`, '_blank');

        setModalMessage('Booking request initiated! Please check your email client to send.');
        setShowModal(true);
        setBookingFormData({ fullName: '', email: '', phoneCode: '+267', customPhoneCode: '', phoneNumber: '', bookedActivities: [], guests: 1 });
    };

    // UseEffect to handle initial scroll if a hash is present in the URL
    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const sectionId = hash.substring(1);
            setActiveSection(sectionId);
            document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    return (
        <div className="min-h-screen bg-stone-100 font-inter text-stone-800 overflow-hidden">
            {/* Header */}
            <header className="bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-lg fixed w-full z-10 top-0 rounded-b-lg flex items-center h-32 md:h-40">
                <div className="container mx-auto flex items-center justify-between w-full px-4 md:px-6">
                    {/* Left side: Logo */}
                    <img
                        src="https://i.imgur.com/kTNF8Os.png"
                        alt="Selerio Turner Safaris Logo"
                        className="h-28 md:h-36 w-auto rounded-lg object-contain"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/128x128/000/FFF?text=Logo'; }}
                    />

                    {/* Navigation Links (Desktop) */}
                    <ul className="hidden md:flex md:space-x-8 lg:space-x-16 md:ml-8 lg:ml-16">
                        <li>
                            <button
                                onClick={() => navigateTo('home')}
                                className={`text-xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-1 px-2 ${activeSection === 'home' ? 'text-amber-300 underline' : 'text-white'}`}
                            >
                                Home
                            </button>
                        </li>
                        <li>
                            <button
                                onClick={() => navigateTo('activities')}
                                className={`text-xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-1 px-2 ${activeSection === 'activities' ? 'text-amber-300 underline' : 'text-white'}`}
                            >
                                Activities
                            </button>
                        </li>
                        <li>
                            <button
                                onClick={() => navigateTo('pricing')}
                                className={`text-xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-1 px-2 ${activeSection === 'pricing' ? 'text-amber-300 underline' : 'text-white'}`}
                            >
                                Pricing
                            </button>
                        </li>
                        <li>
                            <button
                                onClick={() => navigateTo('contact')}
                                className={`text-xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-1 px-2 ${activeSection === 'contact' ? 'text-amber-300 underline' : 'text-white'}`}
                            >
                                Contact Us
                            </button>
                        </li>
                    </ul>

                    {/* Right-aligned Book Now button (Desktop) */}
                    <button
                        onClick={() => navigateTo('booking')}
                        className="ml-auto hidden md:block text-base md:text-lg font-semibold bg-amber-400 text-emerald-900 px-4 py-2 md:px-6 md:py-2 rounded-full hover:bg-amber-300 transition duration-300 shadow-md"
                    >
                        Book Now
                    </button>

                    {/* Mobile Menu Icon (Hamburger) */}
                    <div className="md:hidden ml-4">
                        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white text-3xl focus:outline-none">
                            {isMobileMenuOpen ? <LucideX className="w-8 h-8" /> : <LucideMenu className="w-8 h-8" />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Sidebar Navigation */}
            <div className={`fixed inset-y-0 left-0 w-3/4 max-w-xs bg-gradient-to-br from-blue-800 to-cyan-600 z-50 md:hidden flex flex-col pt-32 transition-transform duration-500 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="container mx-auto px-4 flex flex-col items-start space-y-8 h-full overflow-y-auto">
                    <button
                        onClick={() => navigateTo('home')}
                        className={`text-2xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-2 px-4 ${activeSection === 'home' ? 'text-amber-300 underline' : 'text-white'}`}
                    >
                        Home
                    </button>
                    <button
                        onClick={() => navigateTo('activities')}
                        className={`text-2xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-2 px-4 ${activeSection === 'activities' ? 'text-amber-300 underline' : 'text-white'}`}
                    >
                        Activities
                    </button>
                    <button
                        onClick={() => navigateTo('pricing')}
                        className={`text-2xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-2 px-4 ${activeSection === 'pricing' ? 'text-amber-300 underline' : 'text-white'}`}
                    >
                        Pricing
                    </button>
                    <button
                        onClick={() => navigateTo('booking')}
                        className={`text-2xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-2 px-4 ${activeSection === 'booking' ? 'text-amber-300 underline' : 'text-white'}`}
                    >
                        Book Now
                    </button>
                    <button
                        onClick={() => navigateTo('contact')}
                        className={`text-2xl font-bold hover:text-amber-300 transition duration-300 rounded-lg py-2 px-4 ${activeSection === 'contact' ? 'text-amber-300 underline' : 'text-white'}`}
                    >
                        Contact Us
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay (Click to close) */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-500 ease-in-out opacity-100"
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>
            )}

            {/* Main Content Area - Sections */}
            <main className="pt-32 md:pt-40">
                {/* Home Section */}
                <section
                    id="home"
                    className="min-h-screen flex items-center justify-center bg-cover bg-center text-white rounded-lg scroll-mt-[128px] md:scroll-mt-[160px]"
                    style={{ backgroundImage: "url('https://i.imgur.com/Iugvuen.jpeg')" }}
                >
                    {/* This div applies the consistent container and padding for the content */}
                    <div className="container mx-auto px-4 md:px-6 w-full">
                        {/* This div is the semi-transparent overlay for the text content */}
                        <div className="bg-black bg-opacity-50 p-6 md:p-10 rounded-lg shadow-xl w-full flex flex-col items-center">
                            {/* Heading with Arial font, responsive font size */}
                            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 md:mb-10 rounded-lg text-amber-300 text-center" style={{ fontFamily: 'Arial, sans-serif' }}>Selerio Turner Safaris</h2>
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-10 font-inter">
                                <div className="md:w-1/2 text-center md:text-left">
                                    <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 md:mb-8 rounded-lg text-amber-300 bg-emerald-900 px-4 py-2 inline-block mx-auto">Your satisfaction is our priority.</p>
                                    <p className="text-base sm:text-lg md:text-xl mb-4 md:mb-8 rounded-lg">Embark on an unforgettable journey with our professional guides and witness nature's grandeur up close.</p>
                                </div>
                                <div className="md:w-1/2 text-left">
                                    <p className="text-base sm:text-lg md:text-xl rounded-lg">
                                        Selerio Turner Safaris is a safari company that aims to provide you with a unique, up-standard, and high-quality safari product
                                        offering that does not currently exist in Kasane. Examples include: game drives, riverboat cruises, birdwatching (Chobe, Kasane, Botswana);
                                        recreational fishing and boat transfers (Zambezi river, Namibia).
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Activities Section */}
                <section
                    id="activities"
                    className="py-12 md:py-20 rounded-lg bg-gradient-to-br from-blue-800 to-cyan-600 text-white scroll-mt-[128px] md:scroll-mt-[160px]"
                >
                    <div className="container mx-auto text-center px-4 md:px-6">
                        <h2 className="text-3xl md:text-4xl font-bold mb-8 md:mb-12 rounded-lg">Our Exciting Activities</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
                            {/* New Activity Card 7: Victoria Falls Day Trips */}
                            <div className="relative p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 min-h-[28rem] md:min-h-[36rem] flex items-end overflow-hidden">
                                <video
                                    src="https://i.imgur.com/8q5xeaM.mp4"
                                    loop
                                    muted
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover rounded-lg pointer-events-none"
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/002060/FFFFFF?text=Victoria+Falls'; }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Victoria Falls Day Trip</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Witness the majestic Victoria Falls on an unforgettable day excursion.</p>
                                </div>
                            </div>

                            {/* New Activity Card 6: Boat Transfers */}
                            <div className="relative bg-white p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 min-h-[28rem] md:min-h-[36rem] flex items-end overflow-hidden">
                                <img
                                    src="https://i.imgur.com/34PgXfu.jpeg"
                                    alt="Boat Transfers"
                                    className="absolute inset-0 w-full h-full object-contain transform -rotate-90 rounded-lg"
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/002060/FFFFFF?text=Boat+Transfers'; }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Boat Transfer</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Seamless transfers across the rivers, connecting you to your next adventure.</p>
                                </div>
                            </div>

                            {/* New Activity Card 8: Cultural Village Tours */}
                            <div className="relative p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 min-h-[28rem] md:min-h-[36rem] flex items-end overflow-hidden">
                                <video
                                    src="https://i.imgur.com/f21aziv.mp4"
                                    loop
                                    muted
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover rounded-lg pointer-events-none"
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/002060/FFFFFF?text=Cultural+Tour'; }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Cultural Village Tour</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Immerse yourself in the rich local culture with a village tour.</p>
                                </div>
                            </div>

                            {/* Activity Card 1: Recreational Fishing */}
                            <div className="relative p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 min-h-[28rem] md:min-h-[36rem] flex items-end overflow-hidden">
                                <video
                                    src="https://i.imgur.com/ieSKzRc.mp4"
                                    loop
                                    muted
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover rounded-lg pointer-events-none"
                                ></video>
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Recreational Fishing</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Enjoy a relaxing day of fishing on the Zambezi river, with opportunities to catch various species.</p>
                                </div>
                            </div>
                            {/* Activity Card 2: Riverboat Cruises */}
                            <div className="relative bg-white p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 bg-cover bg-center min-h-[28rem] md:min-h-[36rem] flex items-end"
                                style={{ backgroundImage: "url('https://i.imgur.com/FbeVkuG.jpeg')" }}>
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Riverboat Cruise</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Experience the tranquility of river life and spot wildlife along the Chobe and Zambezi rivers.</p>
                                </div>
                            </div>
                            {/* Activity Card 3: Birdwatching */}
                            <div className="relative p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 min-h-[28rem] md:min-h-[36rem] flex items-end overflow-hidden">
                                <video
                                    src="https://i.imgur.com/kPc1xcW.mp4"
                                    loop
                                    muted
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover rounded-lg pointer-events-none"
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/002060/FFFFFF?text=Bird+Watching'; }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Birdwatching</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Discover a vibrant array of bird species in the rich ecosystems of Chobe and Kasane.</p>
                                </div>
                            </div>
                            {/* Activity Card 4: Game Drives */}
                            <div className="relative bg-white p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 bg-cover bg-center min-h-[28rem] md:min-h-[36rem] flex items-end"
                                style={{ backgroundImage: "url('https://i.imgur.com/CLbN7IZ.jpeg')" }}>
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Game drive</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Witness the Big Five and other incredible wildlife from the comfort of our custom safari vehicles.</p>
                                </div>
                            </div>
                            {/* Activity Card 5: Mobile Safaris */}
                            <div className="relative bg-white p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 bg-cover bg-center min-h-[28rem] md:min-h-[36rem] flex items-end"
                                style={{ backgroundImage: "url('https://i.imgur.com/gIFHDRX.jpeg')" }}>
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Mobile Safari</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Embark on an adventurous journey through the wilderness with our fully equipped mobile camps.</p>
                                </div>
                            </div>

                            {/* New Activity Card: Accommodation Bookings & Airport Transfers - Moved to last position */}
                            <div className="relative p-4 md:p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-2 min-h-[28rem] md:min-h-[36rem] flex flex-col justify-start overflow-hidden bg-emerald-900">
                                <div className="relative z-10 p-2 md:p-4 text-left w-full">
                                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 text-amber-300 rounded-lg">What We Offer Beyond Safari Activities</h2>
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Accommodation Booking</h3>
                                    <p className="text-lg md:text-xl text-stone-100 mb-4 rounded-lg">Find the perfect stay for your safari adventure, from luxury lodges to cozy camps.</p>
                                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 md:mb-2 text-white rounded-lg">Airport Transfer</h3>
                                    <p className="text-lg md:text-xl text-stone-100 rounded-lg">Seamless and comfortable transfers to and from the airport, ensuring a stress-free start and end to your journey.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section id="pricing" className="py-12 md:py-20 bg-[#f4b083] rounded-lg scroll-mt-[128px] md:scroll-mt-[160px]">
                    <div className="container mx-auto text-center px-4 md:px-6">
                        <h2 className="text-3xl md:text-4xl font-bold mb-8 md:mb-12 text-emerald-900 rounded-lg">Our Safari Packages</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 mb-12 md:mb-16">

                            {/* New Pricing Card: Accommodation Bookings */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Accommodation Booking</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 650</span>
                                    <span className="block">USD 50</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person per night</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Personalized Accommodation Search</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Best Rates Guaranteed</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Hassle-Free Booking</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* New Pricing Card: Airport Transfers */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Airport Transfer</h3>
                                <div className="text-left text-stone-700 mb-6 md:mb-8 space-y-4">
                                    <div className="flex items-center">
                                        <span className="text-emerald-600 mr-2 text-2xl">•</span> {/* Circular bullet */}
                                        <p className="text-base md:text-xl font-bold text-stone-700 mb-2 rounded-lg">
                                            <span className="block text-base md:text-lg text-stone-500">Kasane Airport to Any Hotel/Lodge in Kasane/Kazungula:</span>
                                            <span className="block">BWP 270</span>
                                            <span className="block">USD 20</span>
                                            <span className="block text-lg md:text-xl text-stone-500">per person</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center">
                                        <span className="text-emerald-600 mr-2 text-2xl">•</span> {/* Circular bullet */}
                                        <p className="text-base md:text-xl font-bold text-stone-700 mb-2 rounded-lg">
                                            <span className="block text-base md:text-lg text-stone-500">Any Hotel/Lodge in Kasane/Kazungula to Victoria Falls Airport:</span>
                                            <span className="block">BWP 880</span>
                                            <span className="block">USD 65</span>
                                            <span className="block text-lg md:text-xl text-stone-500">per person</span>
                                        </p>
                                    </div>
                                </div>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Comfortable Vehicle</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Driver</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Timely Service</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* Pricing Card: Riverboat Cruises */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Riverboat Cruise</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 500</span>
                                    <span className="block">USD 40</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person for 3-hrs</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> 3-Hour Riverboat Cruise</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Wildlife Viewing</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Guide</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* Pricing Card: Game Drives */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Game drive</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 500</span>
                                    <span className="block">USD 40</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person for 3-hrs</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> 3-Hour Game Drive</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Wildlife Viewing</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Guide</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* Pricing Card: Birdwatching */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Birdwatching</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 500</span>
                                    <span className="block">USD 40</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person for 3-hrs</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> 3-Hour Birdwatching Cruise</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Diverse Bird Species</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Guide</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>


                            {/* New Pricing Card: Recreational Fishing */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Recreational Fishing</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 1690</span>
                                    <span className="block">USD 125</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person, for 3-hrs</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> 3-Hour Fishing Cruise</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Equipment Provided</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Guide</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* New Pricing Card: Mobile Safaris */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Mobile Safari</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 2700</span>
                                    <span className="block">USD 200</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person, per night</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> All-Inclusive Mobile Camp</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Guide</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Meals and Drinks Included</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* New Pricing Card: Boat Transfers */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Boat Transfer</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 2170</span>
                                    <span className="block">USD 160</span>
                                    <span className="block text-base md:text-xl text-stone-500">for a minimum of 4 people</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Comfortable Boat Ride</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Professional Guide</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Scenic River Views</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* New Pricing Card: Victoria Falls Day Trips */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Victoria Falls Day Trip</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 540</span>
                                    <span className="block">USD 40</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person, full day</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Return Transfers</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Guided Tour of the Falls</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Refreshments Included</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>

                            {/* New Pricing Card: Cultural Village Tours */}
                            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border-2 border-emerald-300 transform hover:scale-105 transition duration-300 min-h-[28rem]">
                                <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-emerald-800 rounded-lg">Cultural Village Tour</h3>
                                <p className="text-xl md:text-2xl font-bold text-stone-700 mb-4 md:mb-6 rounded-lg">
                                    <span className="block">BWP 610</span>
                                    <span className="block">USD 45</span>
                                    <span className="block text-base md:text-xl text-stone-500">per person for 1-hr</span>
                                </p>
                                <ul className="text-left text-stone-700 mb-6 md:mb-8 space-y-2 text-sm md:text-base">
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Immersive Cultural Experience</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Local Interaction</li>
                                    <li className="flex items-center rounded-lg"><svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Traditional Activities</li>
                                </ul>
                                <button onClick={() => navigateTo('contact')} className="bg-emerald-800 text-white px-6 py-2 md:px-8 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md">
                                    Inquire Now
                                </button>
                            </div>
                        </div>

                        {/* Removed Additional Services Information */}
                        {/*
                        <div className="max-w-full md:max-w-3xl mx-auto bg-emerald-700 text-white p-6 md:p-8 rounded-lg shadow-lg mb-8 md:mb-12">
                            <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Beyond the Safari</h3>
                            <p className="text-base md:text-lg rounded-lg">
                                We also offer comprehensive services including airport meet-and-greets, comfortable lodging,
                                and personalized assistance to get you settled for your adventure.
                            </p>
                        </div>
                        */}
                    </div>
                </section>

                {/* New Booking Section */}
                <section id="booking" className="py-12 md:py-20 bg-stone-200 rounded-lg scroll-mt-[128px] md:scroll-mt-[160px]">
                    <div className="container mx-auto text-center px-4 md:px-6">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-emerald-900 rounded-lg">Book Your Safari Adventure</h2>
                        <p className="text-base md:text-lg mb-6 md:mb-8 text-stone-700 rounded-lg">Fill out the form below to request your booking. We will get back to you shortly to confirm details!</p>
                        <div id="booking-form" className="max-w-full md:max-w-2xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
                            <form onSubmit={handleBookingSubmit} className="space-y-4 md:space-y-6">
                                <div>
                                    <label htmlFor="fullName" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Full Name</label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        value={bookingFormData.fullName}
                                        onChange={(e) => setBookingFormData({ ...bookingFormData, fullName: e.target.value })}
                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        pattern="[A-Za-z\s]+" // Allows only alphabetic characters and spaces
                                        title="Please enter only alphabetic characters and spaces."
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={bookingFormData.email}
                                        onChange={(e) => setBookingFormData({ ...bookingFormData, email: e.target.value })}
                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$" // More robust email pattern
                                        title="Please enter a valid email address (e.g., example@domain.com)."
                                        required
                                    />
                                </div>
                                {/* Phone Number with Custom PhoneCodeSelect for Booking Form */}
                                <div>
                                    <label htmlFor="bookingPhoneNumber" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Phone Number</label>
                                    <div className="flex gap-2 w-full">
                                        <PhoneCodeSelect
                                            name="phoneCode"
                                            value={bookingFormData.phoneCode}
                                            onChange={(e) => setBookingFormData({ ...bookingFormData, phoneCode: e.target.value })}
                                            countryCodes={countryCodes}
                                            customPhoneCode={bookingFormData.customPhoneCode}
                                            onCustomPhoneCodeChange={(e) => setBookingFormData({ ...bookingFormData, customPhoneCode: e.target.value })}
                                        />
                                        {bookingFormData.phoneCode === 'Other' && (
                                            <input
                                                type="text"
                                                id="bookingCustomPhoneCode"
                                                name="customPhoneCode"
                                                value={bookingFormData.customPhoneCode}
                                                onChange={(e) => setBookingFormData({ ...bookingFormData, customPhoneCode: e.target.value })}
                                                placeholder="e.g., +123"
                                                className="p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 w-24"
                                                pattern="^\+\d{1,4}$" // Allows + followed by 1 to 4 digits
                                                title="Please enter a valid dial code starting with '+' (e.g., +1, +44)."
                                                required
                                            />
                                        )}
                                        <input
                                            type="tel"
                                            id="bookingPhoneNumber"
                                            name="phoneNumber"
                                            value={bookingFormData.phoneNumber}
                                            onChange={(e) => setBookingFormData({ ...bookingFormData, phoneNumber: e.target.value })}
                                            className="flex-1 p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 min-w-0"
                                            // Removed pattern here to allow more flexible validation via JS
                                            title="Please enter a valid phone number (digits only, correct length for selected country)."
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Booking Details for Selected Activities Group */}
                                <div className="bg-stone-100 p-4 md:p-6 rounded-lg border border-stone-300 space-y-4">
                                    <h3 className="text-lg md:text-xl font-semibold text-emerald-900 text-left mb-4">Configure Activity Details</h3>
                                    <div>
                                        <label htmlFor="activityName" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">
                                            Select Activity
                                        </label>
                                        <select
                                            id="activityName"
                                            name="activityName"
                                            value={currentActivitySelection.activityName}
                                            onChange={handleCurrentActivityInputChange}
                                            className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                            required={bookingFormData.bookedActivities.length === 0}
                                        >
                                            <option value="">
                                                {bookingFormData.bookedActivities.length > 0 && currentActivitySelection.activityName === ''
                                                    ? "Select another activity to book or proceed to finish adding \"Number of Guests\" and submit bookings"
                                                    : "Choose an activity"}
                                            </option>
                                            {availableActivities.map((activityOption) => (
                                                <option key={activityOption} value={activityOption}>
                                                    {activityOption}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Conditional fields based on activity type */}
                                    {currentActivitySelection.activityName === 'Airport Transfer' ? (
                                        <>
                                            <div>
                                                <label htmlFor="transferDate" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Date</label>
                                                <input
                                                    type="date"
                                                    id="transferDate"
                                                    name="transferDate"
                                                    value={currentActivitySelection.transferDate}
                                                    onChange={handleCurrentActivityInputChange}
                                                    className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="transferTime" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Time</label>
                                                <input
                                                    type="time"
                                                    id="transferTime"
                                                    name="transferTime"
                                                    value={currentActivitySelection.transferTime}
                                                    onChange={handleCurrentActivityInputChange}
                                                    className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="fromLocation" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">From</label>
                                                <select
                                                    id="fromLocation"
                                                    name="fromLocation"
                                                    value={currentActivitySelection.fromLocation}
                                                    onChange={handleCurrentActivityInputChange}
                                                    className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                    required
                                                >
                                                    <option value="">Select origin</option>
                                                    {fromTransferLocations.map((location) => (
                                                        <option key={location} value={location}>{location}</option>
                                                    ))}
                                                </select>
                                                {currentActivitySelection.fromLocation === SPECIFY_HOTEL_LODGE_OPTION && (
                                                    <input
                                                        type="text"
                                                        id="customFromLocation"
                                                        name="customFromLocation"
                                                        value={currentActivitySelection.customFromLocation}
                                                        onChange={handleCurrentActivityInputChange}
                                                        placeholder="Enter hotel/lodge name"
                                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 mt-2"
                                                        required
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="toLocation" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">To</label>
                                                <select
                                                    id="toLocation"
                                                    name="toLocation"
                                                    value={currentActivitySelection.toLocation}
                                                    onChange={handleCurrentActivityInputChange}
                                                    className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                    required
                                                >
                                                    <option value="">Select destination</option>
                                                    {toTransferLocations.map((location) => (
                                                        <option key={location} value={location}>{location}</option>
                                                    ))}
                                                </select>
                                                {currentActivitySelection.toLocation === SPECIFY_HOTEL_LODGE_OPTION && (
                                                    <input
                                                        type="text"
                                                        id="customToLocation"
                                                        name="customToLocation"
                                                        value={currentActivitySelection.customToLocation}
                                                        onChange={handleCurrentActivityInputChange}
                                                        placeholder="Enter hotel/lodge name"
                                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 mt-2"
                                                        required
                                                    />
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        currentActivitySelection.activityName && ( // Only show if an activity is selected and not Airport Transfers
                                            <>
                                                {currentActivityRequiresTimeSlot && (
                                                    <div>
                                                        <label htmlFor="timeSlot" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Preferred Time Slot</label>
                                                        <select
                                                            id="timeSlot"
                                                            name="timeSlot"
                                                            value={currentActivitySelection.timeSlot}
                                                            onChange={handleCurrentActivityInputChange}
                                                            className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                            required={currentActivityRequiresTimeSlot}
                                                        >
                                                            <option value="">Select a time slot</option>
                                                            <option value="Early Morning">Early Morning</option>
                                                            <option value="Afternoon">Afternoon</option>
                                                            <option value="Sunset">Sunset</option>
                                                        </select>
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                    <div>
                                                        <label htmlFor="startDate" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Start Date</label>
                                                        <input
                                                            type="date"
                                                            id="startDate"
                                                            name="startDate"
                                                            value={currentActivitySelection.startDate}
                                                            onChange={handleCurrentActivityInputChange}
                                                            className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="endDate" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">End Date</label>
                                                        <input
                                                            type="date"
                                                            id="endDate"
                                                            name="endDate"
                                                            value={currentActivitySelection.endDate}
                                                            onChange={handleCurrentActivityInputChange}
                                                            className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleAddActivity}
                                        className="w-full bg-amber-500 text-emerald-900 px-4 py-2 md:px-6 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-amber-400 transition duration-300 shadow-md"
                                        disabled={!isCurrentActivityTimeDetailsFilled()}
                                    >
                                        Add Activity to Booking ({bookingFormData.bookedActivities.length})
                                    </button>
                                </div>
                                {/* End Booking Details for Selected Activities Group */}

                                {/* Display Added Activities */}
                                {bookingFormData.bookedActivities.length > 0 && (
                                    <div className="bg-stone-100 p-4 md:p-6 rounded-lg border border-stone-300 space-y-3">
                                        <h3 className="text-lg md:text-xl font-semibold text-emerald-900 text-left mb-4">Your Current Booking</h3>
                                        <ul className="space-y-3">
                                            {bookingFormData.bookedActivities.map((activity, index) => (
                                                <li key={index} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                                                    <div className="flex-grow text-left">
                                                        <p className="text-stone-700 text-base md:text-lg mb-1">
                                                            <strong className="text-emerald-800">Activity:</strong> {activity.activityName}
                                                        </p>
                                                        {activity.activityName === 'Airport Transfer' ? (
                                                            <>
                                                                <p className="text-stone-700 text-sm md:text-base mb-1">
                                                                    <strong className="text-emerald-800">Date:</strong> {formatDate(activity.transferDate)}
                                                                </p>
                                                                <p className="text-stone-700 text-sm md:text-base mb-1">
                                                                    <strong className="text-emerald-800">Time:</strong> {activity.transferTime}
                                                                </p>
                                                                <p className="text-stone-700 text-sm md:text-base mb-1">
                                                                    <strong className="text-emerald-800">From:</strong> {activity.fromLocation === SPECIFY_HOTEL_LODGE_OPTION ? activity.customFromLocation : activity.fromLocation}
                                                                </p>
                                                                <p className="text-stone-700 text-sm md:text-base">
                                                                    <strong className="text-emerald-800">To:</strong> {activity.toLocation === SPECIFY_HOTEL_LODGE_OPTION ? activity.customToLocation : activity.toLocation}
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="text-stone-700 text-sm md:text-base mb-1">
                                                                    <strong className="text-emerald-800">Dates:</strong> {formatDate(activity.startDate)} to {formatDate(activity.endDate)}
                                                                </p>
                                                                {activity.timeSlot && (
                                                                    <p className="text-stone-700 text-sm md:text-base">
                                                                        <strong className="text-emerald-800">Time:</strong> {activity.timeSlot}
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveActivity(index)}
                                                        className="mt-3 sm:mt-0 ml-0 sm:ml-4 text-red-600 hover:text-red-800 transition duration-300 p-1 rounded-full hover:bg-red-100 flex-shrink-0"
                                                        aria-label={`Remove ${activity.activityName}`}
                                                    >
                                                        <LucideTrash2 className="w-5 h-5" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="guests" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Number of Guests</label>
                                    <input
                                        type="number"
                                        id="guests"
                                        name="guests"
                                        value={bookingFormData.guests}
                                        onChange={(e) => setBookingFormData({ ...bookingFormData, guests: e.target.value })}
                                        min="1"
                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-emerald-800 text-white px-4 py-2 md:px-6 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md"
                                    disabled={!isBookingFormFullyValid()} // Dynamically disable based on all validations
                                >
                                    Submit Booking Request
                                </button>
                            </form>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section id="contact" className="py-12 md:py-20 bg-stone-200 rounded-lg scroll-mt-[128px] md:scroll-mt-[160px]">
                    <div className="container mx-auto text-center px-4 md:px-6">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-emerald-900 rounded-lg">Get in Touch</h2>
                        <p className="text-base md:text-lg mb-6 md:mb-8 text-stone-700 rounded-lg">Have questions or need a custom safari? Contact us directly!</p>
                        <div className="max-w-full md:max-w-2xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
                            <form onSubmit={handleContactSubmit} className="space-y-4 md:space-y-6">
                                <div>
                                    <label htmlFor="contactName" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Your Name</label>
                                    <input
                                        type="text"
                                        id="contactName"
                                        name="name"
                                        value={contactFormData.name}
                                        onChange={handleContactInputChange}
                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        pattern="[A-Za-z\s]+" // Allows only alphabetic characters and spaces
                                        title="Please enter only alphabetic characters and spaces."
                                        required
                                    />
                                </div>
                                {/* Phone Number with Custom PhoneCodeSelect for Contact Form */}
                                <div>
                                    <label htmlFor="contactPhoneNumber" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Phone Number</label>
                                    <div className="flex gap-2 w-full">
                                        <PhoneCodeSelect
                                            name="phoneCode"
                                            value={contactFormData.phoneCode}
                                            onChange={handleContactInputChange}
                                            countryCodes={countryCodes}
                                            customPhoneCode={contactFormData.customPhoneCode}
                                            onCustomPhoneCodeChange={handleContactInputChange}
                                        />
                                        {contactFormData.phoneCode === 'Other' && (
                                            <input
                                                type="text"
                                                id="contactCustomPhoneCode"
                                                name="customPhoneCode"
                                                value={contactFormData.customPhoneCode}
                                                onChange={handleContactInputChange}
                                                placeholder="e.g., +123"
                                                className="p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 w-24"
                                                pattern="^\+\d{1,4}$" // Allows + followed by 1 to 4 digits
                                                title="Please enter a valid dial code starting with '+' (e.g., +1, +44)."
                                                required
                                            />
                                        )}
                                        <input
                                            type="tel"
                                            id="contactPhoneNumber"
                                            name="phoneNumber"
                                            value={contactFormData.phoneNumber}
                                            onChange={handleContactInputChange}
                                            className="flex-1 p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 min-w-0"
                                            // Removed pattern here to allow more flexible validation via JS
                                            title="Please enter a valid phone number (digits only, correct length for selected country)."
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="contactEmail" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Your Email</label>
                                    <input
                                        type="email"
                                        id="contactEmail"
                                        name="email"
                                        value={contactFormData.email}
                                        onChange={handleContactInputChange}
                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$" // More robust email pattern
                                        title="Please enter a valid email address (e.g., example@domain.com)."
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contactMessage" className="block text-left text-stone-700 text-sm md:text-base font-semibold mb-2 rounded-lg">Your Message</label>
                                    <textarea
                                        id="contactMessage"
                                        name="message"
                                        value={contactFormData.message}
                                        onChange={handleContactInputChange}
                                        rows="5"
                                        className="w-full p-2 md:p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        required
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-emerald-800 text-white px-4 py-2 md:px-6 md:py-3 rounded-full text-base md:text-lg font-semibold hover:bg-emerald-700 transition duration-300 shadow-md"
                                >
                                    Send Message
                                </button>
                            </form>
                        </div>
                        <div className="mt-8 md:mt-10 flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
                            <p className="text-xl md:text-2xl font-semibold text-stone-700 rounded-lg">Or reach us directly:</p>
                            <a href="mailto:Selerioturner@gmail.com" className="flex items-center bg-amber-400 text-emerald-900 px-4 py-2 md:px-6 md:py-3 rounded-full text-lg md:text-xl font-semibold hover:bg-amber-300 transition duration-300 shadow-md">
                                <svg className="w-8 h-8 md:w-10 md:h-10 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884zM18 8.118l-8 4-8-4V18a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path></svg>
                                Selerioturner@gmail.com
                            </a>
                            {/* WhatsApp button removed as per user request */}
                            {/*
                            <a href="https://wa.me/26772154495" target="_blank" rel="noopener noreferrer" className="flex items-center bg-green-500 text-white px-4 py-2 md:px-6 md:py-3 rounded-full text-lg md:text-xl font-semibold hover:bg-green-600 transition duration-300 shadow-md">
                                <svg className="w-8 h-8 md:w-10 md:h-10 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.04 2c-5.45 0-9.91 4.46-9.91 9.91 0 1.75.46 3.42 1.34 4.88L2 22l5.24-1.38c1.42.78 3.03 1.2 4.76 1.2 5.45 0 9.91-4.46 9.91-9.91C21.95 6.46 17.49 2 12.04 2zm5.47 14.82c-.17.29-.36.32-.65.35-.29.03-.64-.08-1.05-.22-.4-.14-1.92-.74-2.22-.84-.3-.1-.52-.15-.74.15-.22.3-.85 1.05-1.04 1.25-.19.2-.39.22-.72.08-.32-.14-1.37-.5-2.6-1.6-1.02-.9-1.7-2.28-1.9-2.6-.2-.32-.02-.49.14-.65.15-.15.3-.29.45-.44.15-.15.2-.29.3-.49.1-.19.05-.36-.02-.52-.08-.17-.74-1.78-1.02-2.44-.28-.66-.57-.57-.78-.58-.2-.02-.44-.02-.65-.02-.22 0-.57.08-.87.4-.3.32-1.15 1.12-1.15 2.73 0 1.6 1.18 3.17 1.34 3.37.17.2 2.31 3.53 5.59 4.83 2.94 1.15 3.53.94 4.19.88.66-.06 1.79-.74 2.04-1.45.25-.71.25-1.31.18-1.45-.08-.14-.29-.22-.6-.37z"></path></svg>
                                Chat on WhatsApp
                            </a>
                            */}
                        </div>
                    </div>
                </section>
            </main>

            {/* New Footer with detailed contact information */}
            <footer className="bg-gradient-to-r from-blue-600 to-blue-400 text-white py-8 md:py-12 rounded-t-lg">
                <div className="container mx-auto text-center px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
                        {/* Address Column */}
                        <div className="flex flex-col items-center">
                            <h4 className="text-2xl md:text-3xl font-bold mb-2 md:mb-3">Office Address</h4>
                            <p className="text-xl md:text-2xl">Tribal Lot, 2296, Kasane, Botswana</p>
                        </div>
                        {/* Email Column */}
                        <div className="flex flex-col items-center">
                            <h4 className="text-2xl md:text-3xl font-bold mb-2 md:mb-3">Email Us</h4>
                            <a href="mailto:Selerioturner@gmail.com" className="text-xl md:text-2xl hover:text-amber-300 transition duration-300">
                                Selerioturner@gmail.com
                            </a>
                        </div>
                        {/* WhatsApp/Contact Column */}
                        <div className="flex flex-col items-center">
                            <h4 className="text-2xl md:text-3xl font-bold mb-2 md:mb-3">Phone/Whatsapp</h4>
                            <a href="https://wa.me/26772154495" target="_blank" rel="noopener noreferrer" className="text-xl md:text-2xl hover:text-amber-300 transition duration-300">
                                +267 72 154 495
                            </a>
                            <a href="https://wa.me/26771577232" target="_blank" rel="noopener noreferrer" className="text-xl md:text-2xl hover:text-amber-300 transition duration-300">
                                +267 71 577 232
                            </a>
                        </div>
                    </div>
                    <div className="flex justify-center space-x-4 md:space-x-6 mt-4">
                        <a href="https://www.facebook.com/profile.php?id=61578304850526" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition duration-300">
                            <LucideFacebook className="w-10 h-10 md:w-12 md:h-12" />
                        </a>
                        <a href="https://instagram.com/selerioturnersafaris" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition duration-300">
                            <LucideInstagram className="w-10 h-10 md:w-12 md:h-12" />
                        </a>
                    </div>
                    <p className="text-xl md:text-2xl rounded-lg mt-4">&copy; {new Date().getFullYear()} Selerio Turner Safaris. All rights reserved.</p>
                </div>
            </footer>

            {/* Modal for messages */}
            {showModal && <Modal message={modalMessage} onClose={() => setShowModal(false)} />}
        </div>
    );
};

export default App;
